// src/components/common/ImageUpload.tsx
import React, { useState, useRef, useCallback } from 'react';
import {
    Box,
    Button,
    IconButton,
    Typography,
    LinearProgress,
    Alert,
    alpha,
    Tooltip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ImageIcon from '@mui/icons-material/Image';
import { useTranslation } from 'react-i18next';

export interface ImageUploadProps {
    /** Current image URL (from server) */
    currentImageUrl?: string | null;
    /** Called when user selects and confirms upload */
    onUpload: (file: File, onProgress: (progress: number) => void) => Promise<void>;
    /** Called when user clicks delete (only shown if currentImageUrl exists) */
    onDelete?: () => Promise<void>;
    /** Maximum file size in MB (default: 10) */
    maxSizeMB?: number;
    /** Accepted MIME types (default: image/jpeg, image/png, image/webp) */
    acceptedTypes?: string[];
    /** Label displayed above the component */
    label?: string;
    /** Helper text displayed below */
    helperText?: string;
    /** Disable all interactions */
    disabled?: boolean;
    /** Shape of the preview area */
    shape?: 'square' | 'circle' | 'rectangle';
    /** Width of the component (default: 200 for square/circle, 100% for rectangle) */
    width?: number | string;
    /** Height of the component (default: 200 for square/circle, 150 for rectangle) */
    height?: number | string;
}

const DEFAULT_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const DEFAULT_MAX_SIZE_MB = 10;

const ImageUpload: React.FC<ImageUploadProps> = ({
    currentImageUrl,
    onUpload,
    onDelete,
    maxSizeMB = DEFAULT_MAX_SIZE_MB,
    acceptedTypes = DEFAULT_ACCEPTED_TYPES,
    label,
    helperText,
    disabled = false,
    shape = 'square',
    width,
    height,
}) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);

    // States
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Computed dimensions
    const computedWidth = width || (shape === 'rectangle' ? '100%' : 200);
    const computedHeight = height || (shape === 'rectangle' ? 150 : 200);

    // Border radius based on shape
    const borderRadius = shape === 'circle' ? '50%' : 8;

    // The image to display: preview (selected file) > current (from server) > placeholder
    const displayImageUrl = previewUrl || currentImageUrl;

    // Format accepted types for display
    const acceptedTypesDisplay = acceptedTypes
        .map(type => type.replace('image/', '').toUpperCase())
        .join(', ');

    // Accept attribute for input
    const acceptAttribute = acceptedTypes.join(',');

    const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setError(null);
        setSuccess(null);

        // Validate file type
        if (!acceptedTypes.includes(file.type)) {
            setError(t('imageUpload.errorType', { types: acceptedTypesDisplay }));
            return;
        }

        // Validate file size
        const maxSizeBytes = maxSizeMB * 1024 * 1024;
        if (file.size > maxSizeBytes) {
            setError(t('imageUpload.errorSize', { max: maxSizeMB }));
            return;
        }

        // Create preview URL
        const objectUrl = URL.createObjectURL(file);
        setPreviewUrl(objectUrl);
        setSelectedFile(file);

        // Reset input so same file can be selected again if needed
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [acceptedTypes, acceptedTypesDisplay, maxSizeMB, t]);

    const handleUpload = useCallback(async () => {
        if (!selectedFile) return;

        setUploading(true);
        setUploadProgress(0);
        setError(null);

        try {
            await onUpload(selectedFile, (progress) => {
                setUploadProgress(progress);
            });

            setSuccess(t('imageUpload.successUpload'));
            // Clear preview since the image is now on the server
            // The parent should update currentImageUrl
            setPreviewUrl(null);
            setSelectedFile(null);

            // Clear success message after 3 seconds
            setTimeout(() => setSuccess(null), 3000);
        } catch (err: any) {
            console.error('Upload error:', err);
            setError(err.response?.data?.detail || err.message || t('imageUpload.errorUpload'));
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    }, [selectedFile, onUpload, t]);

    const handleCancelPreview = useCallback(() => {
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl(null);
        setSelectedFile(null);
        setError(null);
    }, [previewUrl]);

    const handleDelete = useCallback(async () => {
        if (!onDelete || !currentImageUrl) return;

        setDeleting(true);
        setError(null);

        try {
            await onDelete();
            setSuccess(t('imageUpload.successDelete'));
            // Clear success message after 3 seconds
            setTimeout(() => setSuccess(null), 3000);
        } catch (err: any) {
            console.error('Delete error:', err);
            setError(err.response?.data?.detail || err.message || t('imageUpload.errorDelete'));
        } finally {
            setDeleting(false);
        }
    }, [onDelete, currentImageUrl, t]);

    const handleClickUploadArea = () => {
        if (!disabled && !uploading && !deleting) {
            fileInputRef.current?.click();
        }
    };

    const isLoading = uploading || deleting;

    return (
        <Box sx={{ width: computedWidth }}>
            {/* Label */}
            {label && (
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {label}
                </Typography>
            )}

            {/* Main upload area */}
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: computedHeight,
                    borderRadius,
                    border: '2px dashed',
                    borderColor: error ? 'error.main' : displayImageUrl ? 'primary.main' : 'grey.300',
                    backgroundColor: displayImageUrl ? 'transparent' : alpha('#2563eb', 0.02),
                    overflow: 'hidden',
                    cursor: disabled || isLoading ? 'default' : 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        borderColor: disabled || isLoading ? undefined : 'primary.main',
                        backgroundColor: disabled || isLoading ? undefined : alpha('#2563eb', 0.05),
                    },
                }}
                onClick={handleClickUploadArea}
            >
                {/* Hidden file input */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={acceptAttribute}
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                    disabled={disabled || isLoading}
                />

                {/* Image display */}
                {displayImageUrl ? (
                    <Box
                        component="img"
                        src={displayImageUrl}
                        alt={label || 'Image'}
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: borderRadius === '50%' ? '50%' : 0,
                        }}
                    />
                ) : (
                    /* Placeholder */
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            color: 'grey.500',
                        }}
                    >
                        <ImageIcon sx={{ fontSize: 48, mb: 1, color: 'grey.400' }} />
                        <Typography variant="body2" color="text.secondary">
                            {t('imageUpload.selectImage')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {acceptedTypesDisplay} - Max {maxSizeMB}MB
                        </Typography>
                    </Box>
                )}

                {/* Overlay with actions when image exists */}
                {displayImageUrl && !isLoading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0,
                            transition: 'opacity 0.2s ease',
                            '&:hover': {
                                opacity: disabled ? 0 : 1,
                            },
                        }}
                    >
                        <Tooltip title={t('imageUpload.changeImage')}>
                            <IconButton
                                sx={{
                                    backgroundColor: 'white',
                                    '&:hover': { backgroundColor: 'grey.100' },
                                    mx: 0.5,
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    fileInputRef.current?.click();
                                }}
                            >
                                <PhotoCameraIcon />
                            </IconButton>
                        </Tooltip>
                        {currentImageUrl && onDelete && !previewUrl && (
                            <Tooltip title={t('imageUpload.deleteImage')}>
                                <IconButton
                                    sx={{
                                        backgroundColor: 'white',
                                        '&:hover': { backgroundColor: 'error.light', color: 'white' },
                                        mx: 0.5,
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete();
                                    }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                )}

                {/* Progress overlay */}
                {uploading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <CloudUploadIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            {t('imageUpload.uploading')} {uploadProgress}%
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={uploadProgress}
                            sx={{ width: '80%', height: 6, borderRadius: 3 }}
                        />
                    </Box>
                )}

                {/* Deleting overlay */}
                {deleting && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <DeleteIcon sx={{ fontSize: 32, color: 'error.main', mb: 1 }} />
                        <Typography variant="body2">
                            {t('imageUpload.deleting')}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Action buttons when file is selected (preview mode) */}
            {selectedFile && !uploading && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleUpload}
                        startIcon={<CloudUploadIcon />}
                        disabled={disabled}
                        fullWidth
                    >
                        {t('imageUpload.confirmUpload')}
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleCancelPreview}
                        disabled={disabled}
                    >
                        {t('imageUpload.cancel')}
                    </Button>
                </Box>
            )}

            {/* Helper text */}
            {helperText && !error && !success && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {helperText}
                </Typography>
            )}

            {/* Error message */}
            {error && (
                <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Success message */}
            {success && (
                <Alert severity="success" sx={{ mt: 1 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}
        </Box>
    );
};

export default ImageUpload;
